import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  fetchTasksMe,
  isTasksLeaderRole,
  RM_TASKS_ENABLED,
  tasksApi,
} from '../../../Tasks/tasksApi';
import {
  Badge,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Table,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Callout } from './ui-pending';
import './TaskTypesPage.scss';

/**
 * Consumer Xperience → Task Settings.
 *
 * What every ticket type is configured to do: how long an RM has, how long
 * until it should be closed, and what happens when the learner's own RM is
 * away. Read-only on purpose — these are seeded, and an edit here would be
 * reverted by the next release.
 *
 * It exists because ops kept asking "what is the SLA for a callback?" and the
 * honest answer lived in a TypeScript file. Showing it settles the question
 * without anyone guessing, and makes a wrong value visible enough to report.
 */

interface Resolution {
  key: string;
  label: string;
  tone?: 'success' | 'fail' | 'neutral';
}
interface TicketConfig {
  activities?: { key: string; label: string }[];
  resolutions?: Resolution[];
  fields?: { key: string; label: string; required?: boolean }[];
  goalResolutionKey?: string;
}
interface TaskTypeRow {
  key: string;
  name: string;
  description?: string | null;
  slaHours?: number | null;
  resolutionHours?: number | null;
  assignmentPolicy?: 'STICK' | 'COVER_IF_ON_LEAVE' | 'COVER_IF_AWAY';
  defaultPriority?: string;
  defaultTeamKey?: string | null;
  goalDescription?: string;
  ticketConfig?: TicketConfig | null;
  taskCount?: number;
  triggers?: string[];
}

/**
 * Hours as ops wrote them. Anything of 24 or more is counted in working
 * *days* by the service, so showing "72h" invites the reading "three shifts",
 * which is not what happens.
 */
const sla = (hours?: number | null): string => {
  if (!hours) return '—';
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days} working day${days === 1 ? '' : 's'}`;
};

/** Who picks it up, in each of the two ways an RM can be absent. */
const WHO: Record<string, { onLeave: boolean; onWeekOff: boolean }> = {
  STICK: { onLeave: false, onWeekOff: false },
  COVER_IF_ON_LEAVE: { onLeave: true, onWeekOff: false },
  COVER_IF_AWAY: { onLeave: true, onWeekOff: true },
};

/**
 * Types the learner's app refuses to raise while their RM is away.
 *
 * The service still accepts them and would still assign them — the block is
 * the app declining to offer the button. Both of these are raised by the
 * learner, so there is nobody else to raise them if the app says no.
 *
 * Shown inside the on-leave and week-off cells, because those are the two
 * columns it contradicts: both would otherwise read "Another RM covers" on a
 * ticket that is never raised at all, which is the kind of thing somebody
 * plans a rota around. Under the type name it was true but easy to miss, and
 * a caveat nobody reads is the same as no caveat.
 */
const BLOCKED_IN_APP: Record<string, string> = {
  callback: 'The app will not offer this while their RM is away.',
  ticket: 'The app will not offer this while their RM is away.',
};

/** One cell: covered by the desk, or held for the learner's own RM. */
const Who: React.FC<{ covered: boolean }> = ({ covered }) => (
  <span className={covered ? 'tt-who tt-who--covered' : 'tt-who'}>
    {covered ? 'Another RM covers' : 'Waits for them'}
  </span>
);

const POLICY: Record<string, { label: string; detail: string; tone: BadgeTone }> = {
  STICK: {
    label: 'Always their own RM',
    detail:
      "The learner's own RM gets it whatever their roster says — on leave, on a week off, " +
      'the ticket waits for them.',
    tone: 'neutral',
  },
  COVER_IF_ON_LEAVE: {
    label: 'Cover when on leave',
    detail:
      "Only while the learner's RM is on leave. A week off is left alone, because the " +
      'deadlines below are already counted in working days and a week off costs them nothing.',
    tone: 'info',
  },
  COVER_IF_AWAY: {
    label: 'Cover when away',
    detail:
      "Whenever the learner's RM is not in today — leave or a week off. The ticket goes to " +
      'whoever on their desk has the lightest queue, and the learner still belongs to their own RM.',
    tone: 'warning',
  },
};

const TaskTypesPage: React.FC = () => {
  const [rows, setRows] = useState<TaskTypeRow[] | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (!RM_TASKS_ENABLED) return;
    (async () => {
      try {
        const me = await fetchTasksMe();
        const leader = isTasksLeaderRole(me?.role);
        setAllowed(leader);
        if (!leader) return;
        setRows(await tasksApi<TaskTypeRow[]>('/v1/task-types'));
      } catch (e: any) {
        setAllowed(false);
        toast.error(e?.message || 'Could not load task settings');
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows ?? [];
    return (rows ?? []).filter(
      (r) =>
        r.key.toLowerCase().includes(needle) || (r.name ?? '').toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const header = (actions?: React.ReactNode) => (
    <PageHeader
      eyebrow="RM Tasks"
      title="Task Settings"
      subtitle="How each ticket type is configured — the time an RM has, the time to close it, and who gets it when the learner's RM is away."
      actions={actions}
    />
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        {header()}
        <EmptyState title="Not enabled" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return (
      <Page>
        {header()}
        <Loader />
      </Page>
    );
  }
  if (!allowed) {
    return (
      <Page>
        {header()}
        <EmptyState title="Leaders only" description="Task settings are for team leaders and admins." />
      </Page>
    );
  }

  return (
    <Page>
      {header(
          <Field label="Find a type" className="tt-search">
            {(a) => (
              <SearchInput
                {...a}
                size="sm"
                placeholder="Find a type…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            )}
          </Field>,
      )}

      {/* Said once, plainly, at the top — so nobody hunts for an edit
          button that was never there. */}
      <Callout tone="info" title="This page is read-only.">
        These settings are part of the release, so an edit here would be undone by the next one.
        To change an SLA, a closing reason, or who covers when an RM is away,{' '}
        <strong>contact the tech team</strong> and it ships with the change.
      </Callout>

      {/* Why the week-off column reads "waits" almost everywhere, and why
          that is not simply meanness: the deadlines beside it already skip
          it. Without this the two columns look arbitrarily different. */}
      <Card>
        <div className="tt-explainer">
          <p className="ds-text-muted">
            A ticket normally waits for the learner&apos;s own RM. Where a type is set to be
            covered, it goes to whoever on that RM&apos;s desk has the lightest queue — and the
            learner still belongs to their own RM, so the arrangement ends when they return.
          </p>
          <p className="ds-text-muted">
            The two are set separately because they cost different things.{' '}
            <strong>A week off is already free</strong>: the deadlines on the left are counted
            in working days, so it is skipped and nothing runs out.{' '}
            <strong>Leave is not</strong> — the clock keeps running while they are gone, which
            is where a missed SLA usually comes from.
          </p>
          <div className="rm-row">
            {(['STICK', 'COVER_IF_ON_LEAVE', 'COVER_IF_AWAY'] as const).map((k) => {
              const used = (rows ?? []).filter((r) => (r.assignmentPolicy ?? 'STICK') === k).length;
              return (
                <span key={k} className="rm-row">
                  <Badge tone={POLICY[k].tone}>{POLICY[k].label}</Badge>
                  <span className="rm-caption">{used === 0 ? '(not used)' : `(${used})`}</span>
                </span>
              );
            })}
          </div>
        </div>
      </Card>

      <Card flush>
        {rows === null ? (
          <Loader label="Loading task types…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={q.trim() ? <>No type matches &ldquo;{q}&rdquo;.</> : 'No task types'}
            description={q.trim() ? 'Try a shorter name or the type key.' : undefined}
          />
        ) : (
          <Table caption="Task types">
            <thead>
              <tr>
                <th rowSpan={2}>Type</th>
                <th rowSpan={2}>Action within</th>
                <th rowSpan={2}>Close within</th>
                {/* "Away" was doing too much work: it meant a week off and
                    leave at once, and those are answered differently. */}
                <th colSpan={2} className="tt-group-head">
                  Who gets it when their RM is…
                </th>
                <th rowSpan={2}>Priority</th>
                <th rowSpan={2} className="ds-num">Tickets</th>
              </tr>
              <tr>
                <th className="tt-subhead">on leave</th>
                <th className="tt-subhead">on a week off</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const policy = POLICY[r.assignmentPolicy ?? 'STICK'] ?? POLICY.STICK;
                const who = WHO[r.assignmentPolicy ?? 'STICK'] ?? WHO.STICK;
                const cfg = r.ticketConfig ?? {};
                const isOpen = open === r.key;
                return (
                  <React.Fragment key={r.key}>
                    <tr
                      className="tt-row"
                      aria-expanded={isOpen}
                      onClick={() => setOpen(isOpen ? null : r.key)}
                    >
                      <td>
                        <div className="ds-strong">{r.name}</div>
                        <div className="rm-caption rm-mono">{r.key}</div>
                      </td>
                      <td className="ds-num tt-left">{sla(r.slaHours)}</td>
                      <td className="ds-num tt-left">{sla(r.resolutionHours)}</td>
                      <td>
                        <Who covered={who.onLeave} />
                        {BLOCKED_IN_APP[r.key] && (
                          <div className="tt-blocked" title={BLOCKED_IN_APP[r.key]}>
                            Blocked in the app
                          </div>
                        )}
                      </td>
                      <td>
                        <Who covered={who.onWeekOff} />
                        {BLOCKED_IN_APP[r.key] && (
                          <div className="tt-blocked" title={BLOCKED_IN_APP[r.key]}>
                            Blocked in the app
                          </div>
                        )}
                      </td>
                      <td className="ds-text-muted">{r.defaultPriority ?? '—'}</td>
                      <td className="ds-num">{r.taskCount ?? 0}</td>
                    </tr>
                    {isOpen && (
                      <tr className="tt-detail-row">
                        <td colSpan={7}>
                          <div className="tt-detail">
                            {r.description && <div>{r.description}</div>}
                            <div className="ds-text-muted">{policy.detail}</div>
                            {r.goalDescription && (
                              <div>
                                <span className="ds-text-muted">Counts as done when: </span>
                                {r.goalDescription}
                              </div>
                            )}
                            {!!cfg.resolutions?.length && (
                              <div>
                                <span className="ds-text-muted">Closing reasons: </span>
                                {cfg.resolutions.map((x) => x.label).join(' · ')}
                              </div>
                            )}
                            {!!cfg.activities?.length && (
                              <div>
                                <span className="ds-text-muted">Activities: </span>
                                {cfg.activities.map((x) => x.label).join(' · ')}
                              </div>
                            )}
                            {!!cfg.fields?.length && (
                              <div>
                                <span className="ds-text-muted">Asks for: </span>
                                {cfg.fields
                                  .map((f) => `${f.label}${f.required ? ' (required)' : ''}`)
                                  .join(' · ')}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <p className="rm-caption">
        A row opens to show its closing reasons and what an RM is asked for. Times of a day or
        more are counted in the RM&apos;s working days, so a week off does not consume them.
      </p>
    </Page>
  );
};

export default TaskTypesPage;
